<template>
  <div class="bravado-combo-box">
    <BravadoText
      v-if="label"
      :id="`${id}-label`"
      variant="body-semibold"
      tag="label"
      class="bravado-combo-box__label"
      @click="onClickContainer"
    >
      {{ label }}
    </BravadoText>
    <div class="bravado-combo-box__input-container">
      <div :class="togglerClasses" @click="onClickContainer">
        <div class="bravado-combo-box__input-wrapper">
          <BravadoComboBoxItem
            v-for="selectedOption in selectedOptions.nonRemovableOptions"
            :key="selectedOption.selectDropdownValue"
            data-test="item-block"
          >
            {{ selectedOption.selectDropdownTitle }}
          </BravadoComboBoxItem>
          <BravadoComboBoxItem
            v-for="selectedOption in selectedOptions.removableOptions"
            :key="selectedOption.selectDropdownValue"
            removable
            data-test="item-block"
            @remove="handleRemoveOption(selectedOption)"
          >
            {{ selectedOption.selectDropdownTitle }}
          </BravadoComboBoxItem>
          <input
            ref="input"
            data-test="input"
            role="combobox"
            type="text"
            :aria-labelledby="`${id}-label`"
            :aria-expanded="showOptions"
            :aria-controls="`${id}-listbox`"
            class="bravado-combo-box__input"
            :value="term"
            :placeholder="placeholder"
            @keyup.enter.space="handleActionKeyUp"
            @input="handleUpdateTerm"
          />
        </div>
        <div v-if="showOptions" class="bravado-combo-box__dropdown">
          <BravadoSelectDropdown v-bind="selectDropdownProps.args" v-on="{ ...selectDropdownProps.listeners }" />
        </div>
      </div>
    </div>
    <BravadoText v-if="isInvalid && errorMessage" variant="caption2" class="bravado-combo-box__error-message">
      {{ errorMessage }}
    </BravadoText>
    <BravadoText v-else-if="hint" variant="caption2" class="bravado-combo-box__hint">{{ hint }}</BravadoText>
  </div>
</template>

<script lang="ts">
import { defineComponent } from '@nuxtjs/composition-api';
import type { PropType } from 'vue';
import type { BravadoSelectDropdownOption } from '../BravadoSelectDropdown/types';
import BravadoSelectDropdown from '../BravadoSelectDropdown/BravadoSelectDropdown.vue';
import BravadoSelectDropdownHandlerMixin from '../BravadoSelectDropdown/BravadoSelectDropdownHandler.mixin';
import BravadoComboBoxItem from './BravadoComboBoxItem.vue';
import { guid } from '~/components/utils/core/uid';

export default defineComponent({
  name: 'BravadoComboBox',
  components: {
    BravadoSelectDropdown,
    BravadoComboBoxItem,
  },
  mixins: [BravadoSelectDropdownHandlerMixin],
  props: {
    term: {
      type: String,
      required: true,
    },
    isCreatable: {
      type: Boolean,
      default: false,
    },
    isTextArea: {
      type: Boolean,
      default: false,
    },
    minLength: {
      type: Number,
      default: 2,
    },
    dropdownPosition: {
      type: String as PropType<'down' | 'up'>,
      default: 'down',
    },
    actionKeyUp: {
      type: String as PropType<'none' | 'enter' | 'space'>,
      default: 'none',
    },
  },
  computed: {
    selectDropdownProps() {
      return {
        args: {
          options: this.sortedOptions,
          selectedOptions: this.value,
          showOptions: this.showOptions,
          selectId: this.id,
          isCreatable: this.isCreatable,
          term: this.term,
          minLength: this.minLength,
          position: this.dropdownPosition,
          getIsRemovable: this.getIsRemovable,
        },
        listeners: {
          'toggle-option': this.handleToggleOptionWrapper,
          'close-options': this.handleCloseOptions,
          'create-option': () => this.handleCreateOption(this.term),
        },
      };
    },
    selectedOptions() {
      const nonRemovableOptions: Array<BravadoSelectDropdownOption> = [];
      const removableOptions: Array<BravadoSelectDropdownOption> = [];

      this.value.forEach(selectedOption => {
        const arrayToPush = this.getIsRemovable(selectedOption) ? removableOptions : nonRemovableOptions;
        arrayToPush.push(selectedOption);
      });

      return {
        nonRemovableOptions,
        removableOptions,
      };
    },
    togglerClasses() {
      return {
        'bravado-combo-box__toggler': true,
        'bravado-combo-box__toggler--error': this.isInvalid,
        'bravado-combo-box__toggler--disabled': this.isDisabled,
        'bravado-combo-box__toggler--text-area': this.isTextArea,
      };
    },
  },
  methods: {
    focusInput() {
      if (!this.$refs.input) return;

      (this.$refs.input as HTMLInputElement).focus();
    },
    openOptions(term: string) {
      if (!this.multi && this.value[0] && this.getIsRemovable(this.value[0])) return;
      if (term.length < this.minLength) return;
      this.showOptions = true;
    },
    onClickContainer() {
      this.focusInput();
      this.openOptions(this.term);
    },
    reset() {
      this.$emit('search', '');
      this.handleCloseOptions();
    },
    handleUpdateTerm(event: InputEvent) {
      const value = (event.target as HTMLInputElement).value;
      this.$emit('search', value);
      this.openOptions(value);
    },
    handleRemoveOption(option: BravadoSelectDropdownOption) {
      const newValue = this.value.filter(
        selectedOption => selectedOption.selectDropdownValue !== option.selectDropdownValue,
      );
      this.$emit('input', newValue);
    },
    handleActionKeyUp(event: KeyboardEvent) {
      const isActionKey =
        this.actionKeyUp !== 'none' &&
        ((event.key === 'Enter' && this.actionKeyUp === 'enter') ||
          (event.key === ' ' && this.actionKeyUp === 'space'));

      if (!isActionKey || !this.isCreatable) {
        return;
      }

      this.handleCreateOption(this.term.trim());
    },
    handleCreateOption(term: string) {
      this.$emit('create-option', {
        selectDropdownTitle: term,
        selectDropdownValue: guid(),
      } as BravadoSelectDropdownOption);
      this.reset();
    },
    handleToggleOptionWrapper(option: BravadoSelectDropdownOption) {
      this.reset();
      this.handleToggleOption(option);
    },
  },
});
</script>

<style scoped lang="scss">
.bravado-combo-box {
  display: flex;
  flex-direction: column;

  $this: &;

  &__label {
    color: var(--theme-color-text);
    margin-bottom: 6px;
  }

  &__input-container {
    position: relative;
    width: 100%;
  }

  &__toggler {
    @include body;
    @include input-focus-transition;

    width: 100%;
    padding: 10px 16px;
    background: var(--theme-color-card);
    border: 1px solid var(--theme-color-border);
    border-radius: 4px;
    outline: none;
    appearance: none;
    height: inherit;
    min-height: inherit;
    cursor: pointer;
    text-align: left;

    &:focus,
    &:focus-within {
      @include input-focus-rules;
    }

    &--error {
      border-color: var(--red-50);
    }

    &--disabled {
      background: var(--theme-color-over-card);
      cursor: not-allowed;
    }

    &--text-area {
      min-height: 130px;

      #{$this}__dropdown {
        left: 16px;
        right: 16px;
        margin-top: 6px;
      }
    }
  }

  &__input-wrapper {
    gap: 8px;
    width: 100%;
    position: relative;
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    align-items: center;
    align-content: baseline;
  }

  &__input {
    appearance: none;
    color: var(--theme-color-text);
    background-color: inherit;
    border: 0;
    outline: none;
    flex: 1;
    padding: 1px 0;

    @include text-overflow;
    @include body;

    &[disabled]::placeholder {
      color: rgba(var(--theme-text-rgb), 0.3);
    }
  }

  &__dropdown {
    position: absolute;
    left: 0;
    right: 0;
    margin-top: 14px;
  }

  &__option-list {
    position: absolute;
  }

  &__error-message {
    color: var(--red-50);
  }

  &__hint {
    color: var(--gray-50);
    margin-top: 6px;
  }
}
</style>
